
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import PageLayout from "@/components/layout/PageLayout";
import { WorkTenderCard } from "@/components/tender-management/WorkTenderCard";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { LegalServicesBanner } from "@/components/legal/LegalServicesBanner";
import { useTranslation } from "react-i18next";
import { TenderData } from "@/types/tender";

const TenderManagement = () => {
  const navigate = useNavigate();
  const {
    toast
  } = useToast();
  const {
    t
  } = useTranslation('tenderManagement');
  const queryClient = useQueryClient();

  // Set up a subscription to listen for tender changes
  useEffect(() => {
    const subscription = supabase.channel('public:saved_tenders').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'saved_tenders'
    }, () => {
      console.log('Tenders table changed, refreshing data...');
      queryClient.invalidateQueries({
        queryKey: ['tenders']
      });
    }).subscribe();
    return () => {
      supabase.removeChannel(subscription);
    };
  }, [queryClient]);

  // Fetch tenders with a simpler query approach
  const {
    data: tenders = [],
    isLoading,
    error
  } = useQuery({
    queryKey: ['tenders'],
    queryFn: async () => {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return [];
      }
      try {
        // First fetch the tenders
        const {
          data: savedTenders,
          error: tendersError
        } = await supabase.from('saved_tenders').select('*').order('created_at', {
          ascending: false
        });
        if (tendersError) {
          console.error('Error fetching tenders:', tendersError);
          toast({
            title: "Error",
            description: "Failed to load your tenders",
            variant: "destructive"
          });
          throw tendersError;
        }
        console.log("Fetched tenders:", savedTenders ? savedTenders.length : 0);

        // Then fetch the associated profiles
        const {
          data: profiles,
          error: profilesError
        } = await supabase.from('profiles').select('id, email, company_name').in('id', savedTenders.map(t => t.user_id));
        if (profilesError) {
          console.error('Error fetching profiles:', profilesError);
          toast({
            title: "Error",
            description: "Failed to load user profiles",
            variant: "destructive"
          });
          throw profilesError;
        }

        // Combine the data - ensure we have well-formed tender objects
        return savedTenders.filter(tender => tender && tender.id) // Filter out any invalid tenders
        .map(tender => {
          // Validate tender_data to prevent rendering issues
          if (tender.tender_data) {
            // Log any tender deadlines for debugging in a safe way
            try {
              // Safely check if tender_data is an object before accessing properties
              const tenderData = tender.tender_data as Record<string, any>;

              // Access properties safely with type checking
              let deadline: string | null | undefined = null;

              // Try different deadline properties that might exist
              if (tenderData.deadline_date) {
                deadline = tenderData.deadline_date as string;
              } else if (Array.isArray(tenderData["deadline-receipt-tender-date-lot"]) && tenderData["deadline-receipt-tender-date-lot"].length > 0) {
                deadline = tenderData["deadline-receipt-tender-date-lot"][0] as string;
              } else if (tenderData.DT) {
                deadline = tenderData.DT as string;
              } else if (tenderData["DT-APPLICATIONS"]) {
                deadline = tenderData["DT-APPLICATIONS"] as string;
              }
              if (deadline) {
                // Just log the raw value without conversion to prevent errors
                console.log(`Raw deadline for tender ${tender.id}:`, deadline);
              }
            } catch (err) {
              console.error(`Error processing tender data for ${tender.id}:`, err);
            }
          }
          return {
            ...tender,
            profiles: profiles.find(p => p.id === tender.user_id)
          };
        });
      } catch (error) {
        console.error("Error in tender fetch:", error);
        return [];
      }
    }
  });
  const handleTenderClick = (tender: any) => {
    navigate(`/offer-management/${tender.id}`);
  };
  const handleTenderDeleted = () => {
    // Force refresh the tenders data
    queryClient.invalidateQueries({
      queryKey: ['tenders']
    });
    toast({
      title: t('common.success'),
      description: t('tenderDeleted')
    });
  };
  if (error) {
    console.error("Error loading tender management page:", error);
  }
  return <PageLayout>
      <div className="container mx-auto py-8">
        <div className="space-y-6">
          <div>
            
            
          </div>

          {/* Legal services banner */}
          <LegalServicesBanner />

          {isLoading ? <div className="text-center py-8">{t('loadingTenders')}</div> : error ? <div className="text-center py-8 text-red-600">
              {t('errors.loadFailed')}
            </div> : <div className="grid gap-4">
              {Array.isArray(tenders) && tenders.length > 0 ? tenders.map(tender => <WorkTenderCard key={tender.id} tender={tender} onClick={() => handleTenderClick(tender)} onDelete={handleTenderDeleted} />) : <div className="text-center py-8 text-gray-500">
                  {t('noSavedTenders')}
                </div>}
            </div>}
        </div>
      </div>
    </PageLayout>;
};
export default TenderManagement;
